import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { NO_WORKSPACE, buildLabel, profileReady, type HelpTab, type HelpWhere } from "@joogoos/contracts";
import { api, ApiError } from "./api.js";
import { NoWorkspace } from "./NoWorkspace.js";
import { useSwitchWorkspace, useWorkspace, useWorkspaces } from "./ui/workspace.js";
import { inWorkspace, pathInWorkspace, workspaceOfPath } from "./ui/address.js";
import { RenameWorkspace } from "./ui/RenameWorkspace.js";
import { useProfile } from "./ui/reads.js";
import { authClient } from "./auth.js";
import { ReviewLogin } from "./ReviewLogin.js";
import { sendArrival } from "./arrival.js";
import { Login } from "./Login.js";
import { Studio } from "./studio/Studio.js";
import { Overview } from "./tabs/Overview.js";
import { Review, type ReviewPlace } from "./review/Review.js";
import { Business, businessViewOf, type BusinessView } from "./tabs/Business.js";
import { Content } from "./tabs/Content.js";
import { Calendar } from "./tabs/Calendar.js";
import { Analytics } from "./tabs/Analytics.js";
import { Customers } from "./tabs/Customers.js";
import { Seo } from "./tabs/Seo.js";
import { Icon, type IconName } from "./ui/icons.js";
import { Logo } from "./ui/logo.js";
import { Account, type Panel } from "./account/Account.js";
import { ReportNews } from "./reports/News.js";
import { HelpDialog } from "./help/HelpDialog.js";
import { setOnScreen } from "./reports/onScreen.js";
import { useNarrow } from "./ui/narrow.js";
import { Activity, useActivity } from "./ui/Activity.js";
import { CreditSpent, useNoCredit } from "./ui/NoCredit.js";

/**
 * Overview first, then Business, because that is the order somebody meets them: what this is and what needs you,
 * then who it is being made for — the Studio is better at everything once it knows that, and a customer who fills
 * it in on the way past never has to be asked again (03 §business context).
 */
const TABS: ReadonlyArray<readonly [string, IconName]> = [
  ["Overview", "home"], ["Business", "shop"], ["Studio", "studio"], ["Content", "content"], ["Calendar", "calendar"], ["Analytics", "analytics"],
  // Customers is about somebody other than this business (23, ADR 0037); SEO is last, after it, because
  // it is the newest tool and because it is the one a customer comes back to least often (ADR 0033,
  // the owner's word 2026-09-20).
  ["Customers", "users"], ["SEO", "seo"],
];
type Tab = (typeof TABS)[number][0];

/**
 * The tab is the address (2026-09-20): `/studio`, `/business`, `/content`, `/calendar`, `/analytics`.
 *
 * It was state alone, which meant every reload put the person back on whichever tab the landing rule chose, and a
 * workspace switch — which is a reload, because nothing cached belongs to the business being arrived at — did the
 * same. Somebody reading their Analytics, switching to their other business to read its Analytics, arrived in the
 * Studio. The address is the one place a tab survives a page being thrown away and built again.
 *
 * It replaces rather than pushes: a tab is where you are, not somewhere you travelled to, so Back still leaves the
 * app as it always has rather than walking through the tabs you have looked at. `/` names no tab and lands by the
 * rule below, which is what every email link (`/?report=…`, `/?export=…`) and a bare joogoos.com still do.
 *
 * The API already serves `index.html` for any path it does not know, so these are real addresses to bookmark and to
 * sign in against — `Login` carries the whole of `pathname` and `search` through the magic link.
 *
 * Business's step follows its tab, `/business/channels` (2026-09-28): a reload on Channels while connecting accounts
 * landed on whichever step the landing rule chose. The Overview keeps one too since 2026-10-02, `/overview/review`:
 * the marketing review is a screen under the Overview rather than a tab of its own (35, ADR 0063), and a reload in the
 * middle of deciding on one must come back to it. Nothing else keeps a step in the address.
 */
const pathOfTab = (t: Tab) => `/${t.toLowerCase()}`;
/** A step a tab keeps in its address: one of Business's, or the Overview's review. */
type Step = BusinessView | "review";
/** Where an address is: its tab and its step. `/w/<id>/studio` is the Studio, and so is an old `/studio` (ADR 0052). */
const whereOfPath = (pathname: string): { tab: Tab | null; step: Step | null } => {
  const [first, second] = pathInWorkspace(pathname).toLowerCase().split("/").filter(Boolean);
  const tab = TABS.find(([t]) => t.toLowerCase() === first)?.[0] ?? null;
  return { tab, step: tab === "Business" ? businessViewOf(second) : tab === "Overview" && second === "review" ? "review" : null };
};

/**
 * Top navigation only (03): tabs on the left, credits and the account button on the right. On a phone the tabs are a
 * bar along the bottom instead, where a thumb reaches them. Eight of them at 375 px is 47 px each, which is
 * tighter than the 56 px [21 §9](../../../docs/21-articles-and-seo.md) asked for and is an open item in
 * [14](../../../docs/14-open-items.md): the bar needs a shape that does not shrink again with the ninth tab.
 */
export function App() {
  const session = authClient.useSession();
  // Where this person came from, handed over the first time they are signed in and never again (`arrival.ts`). Here
  // rather than on the sign-in page, because the browser that lands with the referrer is often not the one that
  // finishes the sign-in: a magic link is opened wherever the mail is read.
  const signedIn = Boolean(session.data);
  useEffect(() => { if (signedIn) void sendArrival(); }, [signedIn]);
  if (session.isPending) return <div className="h-full grid place-items-center text-faint text-sm">…</div>;
  // `/review` is the app reviewer's password sign-in (33), reached only by its address.
  if (!session.data) return location.pathname === "/review" ? <ReviewLogin /> : <Login />;
  const { id, email, name, image } = session.data.user;
  return <Shell userId={id} email={email} name={name || null} hasPicture={Boolean(image)} />;
}

function Shell({ userId, email, name, hasPicture }: { userId: string; email: string; name: string | null; hasPicture: boolean }) {
  /**
   * Where a signed-in person lands.
   *
   * The Studio, normally: it is what the product is for. But every brief is written from the business page, and a
   * Studio that does not know what the business sells is a good screen making generic posts. So the first land goes
   * to **Overview** until the five sections it writes from are answered (03 §business context) — which is the
   * screen that says what this is, what the tabs hold, and that the business page is the thing to do first, with
   * the door to it. From then on a click decides: this is a landing rule, not a lock, and nothing stops anyone
   * opening the Studio anyway. Until 2026-09-20 the first land went straight to Business, which fixed the same
   * problem without ever saying why it was being asked.
   *
   * A profile that could not be read lands on the Studio. A failed request is not a reason to send somebody
   * somewhere they did not ask to go, and neither is a route that is not built.
   */
  const profile = useProfile();
  // Asked once, on the way back from the card processor's page: the two screens that show money read again.
  const qc = useQueryClient();
  // What the address says, before anything has been clicked: a reload, or a link somebody kept.
  const [where, setWhere] = useState(() => whereOfPath(location.pathname));
  const picked = where.tab;
  // A step goes with its tab: another tab is somewhere else, and coming back to Business lands by its own rule. The
  // review is the exception: it has no menu of its own to be on, so the Overview's tab pressed from it is the way back.
  const setPicked = (tab: Tab) => setWhere((w) => (w.tab === tab && w.step !== "review" ? w : { tab, step: null }));
  /**
   * Business on one of its steps, when another tab sends somebody there — Customers → Set up pointing at the mailbox
   * letters leave from, SEO pointing at the address articles are published under (ADR 0040) — or a platform's sign-in
   * comes back.
   */
  const openBusiness = (step: BusinessView) => setWhere({ tab: "Business", step });
  /** Where the review sends somebody: a tab, and on Business one of its steps — where a gap is fixed (`gapSays`). */
  const openFromReview = (to: ReviewPlace, step?: string) => {
    const view = to === "Business" ? businessViewOf(step) : null;
    if (view) openBusiness(view);
    else setPicked(to);
  };
  // Decided on the first answer that arrives and never again — landing is about arriving. Left as a live
  // calculation it would also be a loop: a profile that cannot be read is retried when the Studio mounts and asks
  // for it too, and a screen that unmounts whenever a read is in flight makes it mount again.
  const [landed, setLanded] = useState<Tab | null>(null);
  useEffect(() => {
    if (landed || profile.isPending) return;
    const thin = profile.data?.available === true && !profileReady(profile.data.profile);
    setLanded(thin ? "Overview" : "Studio");
  }, [landed, profile.isPending, profile.data]);
  const tab = picked ?? landed;
  /**
   * The address follows the tab, the landing included, so the next reload comes back to it. Replaced rather than
   * pushed (above), and only when it is not already right, so nothing is written on a click that changes nothing.
   * The query is carried along: `?report=` is dropped by its own effect below, not by this one.
   */
  // The workspace goes in the address too once it is known (ADR 0052), so a reload and a copied link stay in it.
  // By its slug, `/w/overbill/studio`, once the workspace has answered; an address with its id is rewritten to it.
  // Until then the one the address already names is kept, because it is what every request is sent to.
  const here = useWorkspace().data?.slug;
  const step = tab === "Business" || tab === "Overview" ? where.step : null;
  useEffect(() => {
    if (!tab) return;
    const path = `${pathOfTab(tab)}${step ? `/${step}` : ""}`;
    const ws = here ?? workspaceOfPath(location.pathname);
    const want = ws ? inWorkspace(ws, path) : path;
    if (location.pathname !== want) history.replaceState(history.state, "", `${want}${location.search}${location.hash}`);
  }, [tab, step, here]);
  // Which tab this is, for a report filed from it (ADR 0024 §2). The route says it too now, and says it to the desk.
  useEffect(() => { setOnScreen({ screen: tab }); }, [tab]);
  const [menu, setMenu] = useState(false);
  const narrow = useNarrow();
  const [panel, setPanel] = useState<Panel | null>(null);
  // The report the Your reports panel has open, when something other than a click in it chose one.
  const [reportId, setReportId] = useState<number | null>(null);
  const openReport = (id: number) => { setReportId(id); setPanel("reports"); };
  // The help page the Help panel has open, by slug: the Help dialog opens one when it answers from it.
  const [helpSlug, setHelpSlug] = useState<string | null>(null);
  // The workspace Delete was pressed on in the account menu: the panel opens with that row's close already asked.
  const [closingId, setClosingId] = useState<string | null>(null);
  /**
   * Help — asking, and the report form inside it (docs/19, ADR 0024 §2, ADR 0026). It was a button beside the tabs
   * until 2026-09-21; it is asked from the **Help panel of the account dialog** now, beside the pages it answers
   * from, because the menu it was in stopped listing the dialog's panels the same day. It still reads the screen it
   * was opened over (`onScreen`) — the dialog is opened from that screen — so a report filed from here carries the
   * tab and the project, exactly as it did.
   */
  const [helping, setHelping] = useState(false);
  const openHelp = (slug: string) => { setHelpSlug(slug); setPanel("help"); };
  // Where Help points instead of answering (docs/19): an account panel, or a tab. The dialog goes when it points.
  const openPlace = (where: HelpWhere) => {
    const [kind, name] = where.split(":") as [string, string];
    if (kind === "panel") { setReportId(null); setHelpSlug(null); setClosingId(null); setPanel(name as Panel); }
    else if (kind === "tab") { setPanel(null); setPicked(name as Tab); }
  };
  const leaveHelp = <A extends unknown[]>(f: (...a: A) => void) => (...a: A) => { setHelping(false); f(...a); };
  // The export email links to `/?export=<id>` (ADR 0019): it opens the panel the download is on, after sign-in if need
  // be (Login carries the address through). The query is dropped once read, so a reload does not open the dialog again.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    /**
     * Back from a platform's sign-in (`?channel=`, 25) or the mailbox's (`?mailbox=`, 22): the page the button was
     * on, where the panel says in a sentence how it went and takes the word out of the address itself. Without this
     * the landing rule chose, and a connected X arrived in the middle of whatever the Studio had open, saying nothing.
     */
    if (params.has("channel") || params.has("mailbox")) {
      openBusiness(params.has("channel") ? "channels" : "customers");
      return;
    }
    if (params.has("report")) {
      // Every report email links to `/?report=<id>` the same way (ADR 0024): the thread, after sign-in if need be.
      const id = Number(params.get("report"));
      if (Number.isSafeInteger(id) && id > 0) openReport(id);
      params.delete("report");
    } else if (params.has("export")) {
      setPanel("workspace");
      params.delete("export");
    } else if (params.has("topup")) {
      /**
       * Back from the card processor's page (ADR 0012). It opens Credits, which is where somebody who has just paid
       * wants to be standing.
       *
       * **The browser coming back is not how the payment is learned about** — the webhook is
       * (`api/src/routes/stripe.ts`) — so this only asks the two screens that show money to read again. That is
       * also why nothing here trusts `?topup=done` to mean paid: it means the tab came back, which is a different
       * fact, and the balance on the page is the server's answer either way.
       */
      setPanel("credits");
      void qc.invalidateQueries({ queryKey: ["credits"] });
      void qc.invalidateQueries({ queryKey: ["workspace"] });
      // The webhook can land after the tab: ask again, so somebody who has paid is not left looking at $0.
      for (const ms of [2_000, 5_000, 10_000, 20_000]) {
        setTimeout(() => {
          void qc.invalidateQueries({ queryKey: ["credits"] });
          void qc.invalidateQueries({ queryKey: ["workspace"] });
        }, ms);
      }
      params.delete("topup");
    } else {
      return;
    }
    const rest = params.toString();
    history.replaceState(history.state, "", `${location.pathname}${rest ? `?${rest}` : ""}${location.hash}`);
  }, []);
  // The one piece of state the picture needs: a URL that will not load must fall back to initials rather than
  // leaving a broken image in the header. Once it has failed there is no point asking for it again.
  const [pictureFailed, setPictureFailed] = useState(false);
  /**
   * A post started from somewhere other than the Studio — today, "Make this" on an idea read off the website
   * (ADR 0035). The Studio picks the newest draft on its own, which is right when nothing was asked for and wrong
   * here: somebody who pressed a button about their lunch menu must land on that post and not on whatever they had
   * open last. Cleared the moment it is opened, so coming back to the tab later lands where it normally would.
   */
  const [opening, setOpening] = useState<string | null>(null);
  const picture = hasPicture && !pictureFailed;
  const avatar = avatarOf(userId);
  // Asked for once, then again when this project spends something or the tab is come back to — not every five
  // seconds for ever (docs/17 §performance 3). Credits only move when a call is charged, and a call is only charged
  // by work this browser started and is already watching on the event stream; twelve requests a minute to be told
  // the same number is the shape of slowness that never looks like slowness.
  const ws = useWorkspace();
  /**
   * The mark crunches while anything at all is happening, and the indicator beside it says what (27, ADR 0045).
   * Both read one answer, so the two can never disagree about whether the business is being worked on — which they
   * did while the mark read the project list and knew nothing about an article, an export or a letter going out.
   */
  const working = (useActivity().data?.counts.running ?? 0) > 0;
  // No credit, nothing runs (ADR 0048): never bought is the account dialog held on Credits, spent is a strip over the tabs.
  const noCredit = useNoCredit();
  // Nothing open to be in: every workspace of theirs is closed (ADR 0022). After every hook, so the order never changes.
  if (ws.error instanceof ApiError && ws.error.code === NO_WORKSPACE) return <NoWorkspace email={email} />;

  return (
    <div className="h-full flex flex-col">
      <header className="h-12 flex items-center justify-between px-2 bg-panel border-b border-line shrink-0">
        <div className="flex items-center">
          <a
            href="https://joogoos.com"
            className="group pl-2 pr-3 shrink-0"
            title={working ? "Working on it…" : "joogoos.com"}
            aria-label="joogoos.com"
          >
            <Logo size={26} thinking={working} />
          </a>
          {narrow ? null : (
            <nav className="flex">
              {TABS.map(([t, icon]) => (
                <button
                  key={t}
                  onClick={() => setPicked(t)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    tab === t ? "text-ink border-coral" : "text-muted border-transparent hover:text-ink-2"
                  }`}
                >
                  <Icon name={icon} /> {t}
                </button>
              ))}
            </nav>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* What is happening for this business, beside its name, and nothing at all while nothing is (27, ADR 0045). */}
          <Activity onGo={(item) => {
            if (item.projectId && item.tab === "Studio") setOpening(item.projectId);
            if (item.tab) setPicked(item.tab as Tab);
          }} />
          {/* The desk for an operator, beside the business they work on, as Overbill has it (docs/18). A plain link:
              the desk is its own page, and only an answer of `desk: true` from the API draws it. */}
          {ws.data?.desk ? (
            <a href="/desk" className="px-2.5 py-1 rounded-full border border-line text-xs font-medium text-ink-2 hover:text-ink hover:border-ink">
              Desk
            </a>
          ) : null}
          {/* What Joogoos said on this workspace's reports, and nothing until it has said something (ADR 0024, amended
              2026-10-05). Beside the credit, and on a phone too, where the credit is not. */}
          <ReportNews
            onOpen={openReport}
            onAll={() => { setReportId(null); setPanel("reports"); }}
            onSettings={() => setPanel("settings")}
          />
          {/* What is left, and which business you are in. The balance is the person's and the same in every workspace
              (ADR 0006); the name beside it says where the work is going, which somebody with two of them cannot tell
              from the tabs alone. The word "credits" is not written — the coin carries it, and the account menu spells
              it out. On a phone there is room for neither, and both are in the menu. */}
          <span className="hidden sm:flex items-center gap-1.5 text-xs text-muted">
            <Icon name="coin" size={14} />
            <b className="tabular-nums text-ink">${ws.data?.balanceUsd ?? "—"}</b>
            <span className="text-faint">·</span>
            <span className="max-w-44 truncate font-medium text-ink-2">{ws.data?.name ?? "…"}</span>
          </span>
          <div className="relative">
            <button
              onClick={() => setMenu((m) => !m)}
              title={name ?? email}
              aria-label={`Account: ${name ?? email}`}
              className="w-8.5 h-8.5 rounded-full bg-ink text-white grid place-items-center text-xs font-bold tracking-wide overflow-hidden"
            >
              {picture
                ? <img src={avatar} alt="" width={34} height={34} className="w-full h-full object-cover" onError={() => setPictureFailed(true)} />
                : initials(name ?? email)}
            </button>
            {menu
              ? (
                <AccountMenu
                  email={email} name={name} picture={picture ? avatar : null} balance={ws.data?.balanceUsd}
                  onOpen={(p, closing) => { setMenu(false); setReportId(null); setHelpSlug(null); setClosingId(closing ?? null); setPanel(p); }}
                  onClose={() => setMenu(false)}
                />
              )
              : null}
          </div>
        </div>
      </header>

      <Account
        open={panel !== null || noCredit === "first"}
        panel={noCredit === "first" ? "credits" : panel ?? "you"}
        locked={noCredit === "first"}
        email={email}
        name={name}
        onPanel={setPanel}
        report={reportId}
        onReport={setReportId}
        help={helpSlug}
        onHelp={setHelpSlug}
        onAsk={() => setHelping(true)}
        closing={closingId}
        onClosing={setClosingId}
        onClose={() => { setClosingId(null); setPanel(null); }}
      />

      {helping ? (
        <HelpDialog
          onClose={() => setHelping(false)}
          onOpenReport={leaveHelp(openReport)}
          onOpenHelp={leaveHelp(openHelp)}
          onOpenPlace={leaveHelp(openPlace)}
        />
      ) : null}

      {noCredit === "spent" ? <CreditSpent onCredits={() => setPanel("credits")} /> : null}
      <main className="flex-1 min-h-0">
        {tab === null ? <div className="h-full grid place-items-center text-faint text-sm">…</div>
          : noCredit === "first" ? null
          : tab === "Overview" && step === "review" ? (
            <Review onBack={() => setWhere({ tab: "Overview", step: null })} onOpen={(id) => { setOpening(id); setPicked("Studio"); }} onPlace={openFromReview} />
          )
          : tab === "Overview" ? (
            <Overview
              onTab={setPicked} onOpen={(id) => { setOpening(id); setPicked("Studio"); }} onCredits={() => setPanel("credits")}
              onReview={() => setWhere({ tab: "Overview", step: "review" })}
            />
          )
          : tab === "Business" ? <Business step={step === "review" ? null : step} onStep={openBusiness} />
          : tab === "Studio" ? <Studio openId={opening} onOpened={() => setOpening(null)} onSeeAll={() => setPicked("Content")} onBusiness={() => setPicked("Business")} />
          : tab === "Content" ? <Content onOpen={(id) => { setOpening(id); setPicked("Studio"); }} onCalendar={() => setPicked("Calendar")} />
          : tab === "Calendar" ? <Calendar onMake={(id) => { setOpening(id); setPicked("Studio"); }} />
          : tab === "Customers" ? <Customers onMailbox={() => openBusiness("customers")} />
          : tab === "SEO" ? <Seo onBusiness={() => openBusiness("site")} onConnection={() => openBusiness("seo")} />
          : <Analytics onMake={(id) => { setOpening(id); setPicked("Studio"); }} onBusiness={() => setPicked("Business")} />}
      </main>

      {narrow ? (
        // Eight columns in a phone's width is 47 px each, so the label is a size smaller than it was at five and
        // is clipped rather than allowed to spill into its neighbour on the narrowest screens there are.
        <nav aria-label="Tabs" className="shrink-0 grid grid-cols-8 bg-panel border-t border-line pb-[env(safe-area-inset-bottom)]">
          {TABS.map(([t, icon]) => (
            <button
              key={t}
              onClick={() => setPicked(t)}
              aria-current={tab === t ? "page" : undefined}
              className={`min-w-0 flex flex-col items-center gap-0.5 pt-2 pb-1.5 px-0.5 text-[9.5px] font-medium ${tab === t ? "text-ink" : "text-muted"}`}
            >
              <Icon name={icon} size={18} className={tab === t ? "text-coral" : ""} />
              <span className="w-full truncate text-center">{t}</span>
            </button>
          ))}
        </nav>
      ) : null}
    </div>
  );
}

/**
 * The menu under the avatar: who you are, what is left, and the way in to the dialog that holds the rest
 * (`account/Account.tsx`). It deliberately does not try to be the settings screen itself — the old client's did,
 * and every new control made it taller until nothing in it could be found (08).
 *
 * It used to carry a block about autopilot and what a non-human actor may do. Autopilot does not exist, and a menu
 * is a poor place to explain something that does not: the rule it described (publishing always ends in a person
 * pressing the button, ADR 0011) is enforced in the publish dialog, which is where it belongs.
 */
function AccountMenu({ email, name, picture, balance, onOpen, onClose }: {
  /** The picture's address, or null to draw initials. */
  email: string; name: string | null; picture: string | null; balance: string | undefined;
  /** Opens a panel of the dialog, and with a workspace id opens the close of that workspace on its row there. */
  onOpen: (p: Panel, closing?: string) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute right-0 top-11 z-50 w-72 bg-panel border border-line rounded-2xl shadow-[0_14px_40px_rgba(23,23,26,.16)] overflow-hidden">
        <button onClick={() => onOpen("you")} className="w-full flex items-center gap-2.5 px-3.5 pt-3.5 pb-3 text-left hover:bg-page">
          <span className="w-10 h-10 rounded-full bg-ink text-white grid place-items-center font-bold shrink-0 overflow-hidden">
            {picture ? <img src={picture} alt="" width={40} height={40} className="w-full h-full object-cover" /> : initials(name ?? email)}
          </span>
          <span className="min-w-0">
            {name ? <span className="block text-sm font-semibold truncate">{name}</span> : null}
            <span className="block text-xs text-muted truncate">{email}</span>
          </span>
        </button>
        <div className="h-px bg-line" />
        <div className="p-2.5">
          <button onClick={() => onOpen("credits")} className="w-full flex items-center gap-2 bg-tint rounded-[10px] px-2.5 py-2 text-left hover:brightness-[.98]">
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold">${balance ?? "—"} credits</span>
              <span className="block text-[11px] text-muted">Credits are dollars.</span>
            </span>
            <span className="px-3 py-1 rounded-md bg-coral text-white text-[11px] font-semibold">Top up</span>
          </button>
        </div>
        <Switcher onDelete={(id) => onOpen("workspace", id)} />
        <div className="h-px bg-line" />
        {/* Workspace, Settings, Your reports, Help and What's new were five rows here and seven panels in the dialog:
            one list written twice, and one of the two always the older. The dialog is the list; this is the way in. */}
        <button onClick={() => onOpen("settings")} className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-ink-2 hover:bg-page">
          <Icon name="wand" size={15} className="text-muted" /> Settings
        </button>
        <div className="h-px bg-line" />
        <button
          onClick={() => authClient.signOut().then(() => location.reload())}
          className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-ink-2 hover:bg-page"
        >
          <Icon name="out" size={15} className="text-muted" /> Sign out
        </button>
        <Build />
      </div>
    </>
  );
}

/**
 * Which build this is, at the very foot of the menu: `Version 0.7.2 · a1b2c3d`. A shipped dev box names the last release
 * and the commit it shipped; a laptop, given no commit, says `Version 0.0.0-dev` (docs/09). Read from `/health`, so it names the build that is answering and
 * nobody types a number into the web app. Asked once per tab, when the menu first opens: a build does not change under
 * a tab, and a tab that never opens the menu pays nothing (docs/17 §performance 4). Unread, it draws nothing.
 */
function Build() {
  const health = useQuery({ queryKey: ["health"], queryFn: api.health, staleTime: Infinity });
  if (!health.data?.version) return null;
  return <p className="px-3.5 pt-1 pb-3 text-[11px] text-muted select-text">{buildLabel(health.data)}</p>;
}

/**
 * The workspaces, in the menu (ADR 0006 amendment): every one this person has, the one they are in marked, and any
 * other a tap away. Read when the menu opens and not before, so a tab that never opens it pays nothing for it
 * (docs/17 §performance 4).
 *
 * It used to draw nothing for somebody with one business, because a switcher with nowhere to go is furniture. Since
 * 2026-09-20 each row can be renamed and deleted, which a person with one business wants as much as anyone, so the
 * list is drawn whenever there is a workspace to draw. New workspace is still in the Workspace panel, where there is
 * room to type a name.
 *
 * Rename happens here, in place: it is free, instant and typed back just as easily. Delete does not — it opens the
 * Workspace panel on that row, where ADR 0022's two steps and the typed name are. A menu is no place to lose a
 * business from.
 */
function Switcher({ onDelete }: { onDelete: (id: string) => void }) {
  const list = useWorkspaces();
  const go = useSwitchWorkspace();
  const [renaming, setRenaming] = useState<string | null>(null);
  const rows = list.data?.workspaces ?? [];
  if (!rows.length) return null;
  return (
    <>
      <div className="h-px bg-line" />
      <div className="py-1.5" role="group" aria-label="Workspaces">
        {rows.map((w) => renaming === w.id ? (
          <RenameWorkspace key={w.id} id={w.id} name={w.name} onDone={() => setRenaming(null)} small />
        ) : (
          <div key={w.id} className="flex items-center gap-1 pr-2.5 text-sm text-ink-2 hover:bg-page">
            <button
              onClick={() => go.mutate(w.id)}
              disabled={w.active || go.isPending}
              aria-current={w.active ? "true" : undefined}
              className="flex items-center gap-2.5 min-w-0 flex-1 pl-3.5 py-2 text-left disabled:cursor-default"
            >
              <span className="w-6 h-6 rounded-md bg-ink text-white grid place-items-center text-[10px] font-bold shrink-0" aria-hidden>
                {w.name.slice(0, 2).toUpperCase()}
              </span>
              <span className="flex-1 min-w-0 truncate">{w.name}</span>
              {w.active ? <Icon name="check" size={14} className="text-coral shrink-0" /> : null}
            </button>
            <button onClick={() => setRenaming(w.id)} aria-label={`Rename ${w.name}`} title="Rename" className="shrink-0 p-1.5 rounded-lg text-muted hover:text-ink">
              <Icon name="pencil" size={14} />
            </button>
            <button onClick={() => onDelete(w.id)} aria-label={`Delete ${w.name}`} title="Delete" className="shrink-0 p-1.5 rounded-lg text-muted hover:text-bad">
              <Icon name="trash" size={14} />
            </button>
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * The picture is served by us, never by Google (12 §identity): the provider URL stays on the server, so a signed-in
 * browser never has to announce itself to Google on every page load.
 *
 * One address per person. The route answers with whoever is signed in and the browser keeps the answer for an hour,
 * so a single address for everybody showed the last person's face to the next account signed in on the same browser
 * (found on live, 2026-09-28). The id only keys the browser's copy; the server ignores it and never serves anybody
 * else's picture.
 */
const avatarOf = (userId: string) => `/v1/me/avatar?u=${encodeURIComponent(userId)}`;

/** Two letters from a name, one from an address. Whoever we know least about still gets something in the circle. */
function initials(who: string) {
  const parts = who.split(/[\s@._-]+/).filter(Boolean).slice(0, 2);
  return parts.map((p) => p[0]!.toUpperCase()).join("") || "?";
}
